import { CompleteAnimation } from '@/constants/animations';
import useIsDarkMode from '@/hooks/useIsDarkMode';
import { IconComplete, IconIncomplete, IconIncompleteDark, IconProgress } from '@/theme/icons';
import { ProgressStatus } from '@/types/actions';
import rem from '@/utils/rem';
import { Button, ButtonProps, Icon, Tooltip } from '@chakra-ui/react';
import Lottie from 'lottie-react';
import { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type States = (typeof STATES)[number];

type Props = {
  state?: States;
  dimension?: string;
  onClick?: (progressStatus: ProgressStatus) => void;
  blockCompletion?: boolean;
  preUpdate?: boolean;
  progressBackgroundColor?: string;
  readOnly?: boolean;
} & Omit<ButtonProps, 'onClick'>;

const STATES = ['incomplete', 'in-progress', 'complete'] as const;

const INCOMPLETE_STATUS_INDEX = 0;
const COMPLETE_STATUS_INDEX = 2;

const IconProgressState = ({
  state = 'incomplete',
  progressBackgroundColor = 'var(--chakra-colors-background-tertiary)',
  dimension = rem(24),
  onClick,
  blockCompletion,
  preUpdate = true,
  disabled,
  ...rest
}: Props) => {
  const { t } = useTranslation('IconProgressState');
  const [progressState, setProgressState] = useState<States>(state);
  const isDarkMode = useIsDarkMode();
  // Rows that MOUNT already complete render a static checkmark instead of each constructing
  // a Lottie player: with many completed rows/cards on screen, one animation engine per
  // checkmark added seconds before busy pages became responsive. The animation still plays
  // whenever the state becomes complete in-session (a click or an external state change).
  const mountedAsComplete = useRef(state === 'complete');

  // Block completion needs only two states
  const states = useMemo(
    () => (blockCompletion ? STATES.filter((state) => state !== 'in-progress') : STATES),
    [blockCompletion],
  );

  const labels = {
    incomplete: disabled ? t('labels.inProgressDisabled') : t('labels.incomplete'),
    'in-progress': disabled ? t('labels.inProgressDisabled') : t('labels.inProgress'),
    complete: disabled ? t('labels.completeDisabled') : t('labels.complete'),
  };

  if (blockCompletion) labels.incomplete = 'Set as Complete';

  const completeState = useMemo(() => {
    return {
      incomplete: <Icon as={isDarkMode ? IconIncomplete : IconIncompleteDark} boxSize={dimension} />,
      'in-progress': (
        <Icon
          as={IconProgress}
          boxSize={dimension}
          style={{ '--progress-background': progressBackgroundColor } as React.CSSProperties}
        />
      ),
    };
  }, [dimension, isDarkMode, progressBackgroundColor]);

  // Decided at render time (not memoized) so the ref is always current: a row that leaves
  // and re-enters the complete state must animate even though the memo deps didn't change.
  const completeIcon = mountedAsComplete.current ? (
    // Matches the Lottie animation's final frame (#4ade80 circle, white check)
    <Icon as={IconComplete} sx={{ path: { stroke: 'white' } }} boxSize={dimension} color="#4ade80" />
  ) : (
    <Lottie
      animationData={CompleteAnimation}
      loop={false}
      style={{
        width: dimension,
        height: dimension,
      }}
    />
  );

  const handleClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      if (disabled) {
        return;
      }

      // TODO: refactor states handling, too complex and error prone
      const index = (states as string[]).indexOf(progressState) + 1;
      const nextState = index > COMPLETE_STATUS_INDEX ? INCOMPLETE_STATUS_INDEX : index;

      if (preUpdate) {
        mountedAsComplete.current = false; // in-session change: completing should animate
        setProgressState(states[nextState]);
      }

      e.stopPropagation();

      onClick && onClick(states[nextState]);
    },
    [progressState, onClick, states, preUpdate, disabled],
  );

  useEffect(() => {
    if (state !== 'complete') {
      mountedAsComplete.current = false; // once it leaves complete, future completes animate
    }
    setProgressState(state);
  }, [state]);

  return (
    <Tooltip isDisabled={disabled} label={labels[progressState]} placement="bottom">
      <Button
        alignItems="center"
        display="flex"
        minWidth={dimension}
        height={dimension}
        cursor={disabled ? 'default' : 'pointer'}
        aria-label={labels[progressState]}
        isDisabled={disabled}
        onClick={!disabled ? handleClick : undefined}
        variant="unstyled"
        {...rest}
      >
        {progressState === 'complete' ? completeIcon : completeState[progressState]}
      </Button>
    </Tooltip>
  );
};

export default IconProgressState;
